import { parseIsoDate } from "@/features/events/eventDate";
import { PARTY_ADDON_UNIT_PRICE_CENTS, type PartyActivityCatalogItem, type PartyAddonType } from "@/api/types/party";

/**
 * Client-side party-price math — PREVIEW ONLY, mirrors
 * app/services/party_pricing.py exactly. Same house rule as
 * features/bookings/bookingMath.ts / features/quotes/quoteMath.ts: the
 * server is the source of truth for every cent actually stored
 * (PartyBooking's price fields are always the server's own computation)
 * — this exists purely to render a live "here's what this will cost"
 * preview in the create-party form before submit.
 */

export const PARTY_BASE_PRICE_CENTS = 35_000;
export const PARTY_EXTRA_HOUR_CENTS = 7_500;

/** Weekend = Saturday + Sunday (business rule). `Date.getDay()`: 0=Sun, 6=Sat. */
export function isWeekendIso(iso: string): boolean {
  const day = parseIsoDate(iso).getDay();
  return day === 0 || day === 6;
}

export function basePriceCentsPreview(extraHour: boolean): number {
  return PARTY_BASE_PRICE_CENTS + (extraHour ? PARTY_EXTRA_HOUR_CENTS : 0);
}

export function addonsTotalCentsPreview(addons: { addon_type: PartyAddonType; quantity: number }[]): number {
  return addons.reduce((sum, a) => sum + a.quantity * PARTY_ADDON_UNIT_PRICE_CENTS[a.addon_type], 0);
}

export function totalPriceCentsPreview(
  extraHour: boolean,
  addons: { addon_type: PartyAddonType; quantity: number }[],
): number {
  return basePriceCentsPreview(extraHour) + addonsTotalCentsPreview(addons);
}

/**
 * Zone A — mirrors app/services/party_pricing.py::catalog_item_price_cents
 * exactly (PREVIEW ONLY, same house rule as every other export in this
 * file): station -> $0; standard project -> flat base_price_cents;
 * premium project -> base_price_cents + premium_per_kid_surcharge_cents *
 * headcount. A single formula covers all three since
 * premium_per_kid_surcharge_cents is 0 for stations/standard projects.
 * `headcount == null` treats the surcharge as 0, mirroring the backend's
 * own flagged "headcount-None-at-selection-time" behavior.
 */
export function catalogItemPriceCentsPreview(
  item: Pick<PartyActivityCatalogItem, "base_price_cents" | "premium_per_kid_surcharge_cents">,
  headcount: number | null | undefined,
): number {
  return item.base_price_cents + item.premium_per_kid_surcharge_cents * (headcount ?? 0);
}

/** Sum of catalogItemPriceCentsPreview across every selected catalog item. */
export function selectionsTotalCentsPreview(
  selections: Pick<PartyActivityCatalogItem, "base_price_cents" | "premium_per_kid_surcharge_cents">[],
  headcount: number | null | undefined,
): number {
  return selections.reduce((sum, item) => sum + catalogItemPriceCentsPreview(item, headcount), 0);
}

/** Base + extra hour + legacy add-ons + selected catalog items (stations/
 * projects) — the full live preview CreatePartyDialog.tsx renders. */
export function totalPriceCentsPreviewWithSelections(
  extraHour: boolean,
  addons: { addon_type: PartyAddonType; quantity: number }[],
  selections: Pick<PartyActivityCatalogItem, "base_price_cents" | "premium_per_kid_surcharge_cents">[],
  headcount: number | null | undefined,
): number {
  return totalPriceCentsPreview(extraHour, addons) + selectionsTotalCentsPreview(selections, headcount);
}
